import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Customer } from '../types/index';
import { api } from '../services/api';
import { CustomerCreateModal } from '../components/CustomerCreateModal';
import { ProfileEditModal } from '../components/ProfileEditModal';
import './ProfilePage.css';

interface UserProfile {
  id?: string;
  username?: string;
  alias?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  createdAt?: string;
  customers: Customer[];
}

interface NewCustomerData {
  name: string;
  address: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
  };
}

export const ProfilePage = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch user profile
      if (user) {
        setProfile({
          id: user.id,
          username: user.username,
          alias: user.alias,
          email: user.email,
          phone: user.phone,
          firstName: user.firstName,
          lastName: user.lastName,
          customers: user.customers,
        });

      } else {
        setError('Failed to load profile data.');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = async (customerData: NewCustomerData) => {
    // Create the customer
    const customer = await api.createCustomer(customerData);
    
    // Add the user to this newly created customer
    if (profile?.id) {
      await api.addUserToCustomer(profile.id, customer.id);
    }
    
    await fetchProfile(); // Refresh profile to show new customer
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="profile-page">
        <div className="error">
          <p>{error}</p>
          <button onClick={fetchProfile} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const displayName = profile?.firstName && profile?.lastName
    ? `${profile.firstName} ${profile.lastName}`
    : profile?.firstName || profile?.alias || profile?.username || 'User';

  const initials = displayName
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="profile-page">
      <div className="page-header">
        <h1>User Profile</h1>
      </div>

      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar">
              {initials}
            </div>
            <div className="profile-info">
              <h2>{displayName}</h2>
              <p className="profile-email">{profile?.email || profile?.alias || profile?.username}</p>
            </div>
          </div>

          <div className="profile-section">
            <h3>Account Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Username</span>
                <span className="info-value">{profile?.alias || profile?.username || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Role</span>
                <span className="info-value">{profile?.role || 'User'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">First Name</span>
                <span className="info-value">{profile?.firstName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Name</span>
                <span className="info-value">{profile?.lastName || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Email</span>
                <span className="info-value">{profile?.email || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Phone Number</span>
                <span className="info-value">{profile?.phone || 'N/A'}</span>
              </div>
              {profile?.createdAt && (
                <div className="info-item">
                  <span className="info-label">Member Since</span>
                  <span className="info-value">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {profile?.customers && profile.customers.length > 0 && (
            <div className="profile-section">
              <h3>Associated Customers</h3>
              <div className="customers-list">
                {profile.customers.map((customer) => (
                  <div key={customer.id} className="customer-item">
                    <div className="customer-icon">🏢</div>
                    <div className="customer-name">{customer.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="profile-section">
            <h3>Customer Management</h3>
            <div className="customer-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                Request New Customer
              </button>
            </div>
          </div>

          <div className="profile-actions">
            <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
              Edit Profile
            </button>
            <button className="btn btn-secondary" onClick={() => alert('Coming soon!')}>
              Change Password
            </button>
          </div>
        </div>
      </div>

      {/* Customer Create Modal */}
      <CustomerCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateCustomer}
      />

      {/* Profile Edit Modal */}
      {profile && (
        <ProfileEditModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          profile={profile}
          onSave={fetchProfile}
        />
      )}
    </div>
  );
};
